import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import * as authService from '../../services/authService';
import { RegisterData } from '../../services/authService';
import styles from './AuthPage.module.css';

// نوع البيانات للنموذج، يجمع كل الحقول الممكنة
type FormInputs = Omit<RegisterData, 'registrationType'> & {
  confirmPassword: string;
  registrationType: 'subscriber' | 'trainee' | 'lawyer' | ''; // السماح بقيمة فارغة في البداية
};

const RegisterPage: React.FC = () => {
  const [isSuccess, setIsSuccess] = useState(false);
  const { 
    register, 
    handleSubmit, 
    formState: { errors, isSubmitting }, 
    watch 
  } = useForm<FormInputs>({
    defaultValues: {
      registrationType: '', // ابدأ بدون اختيار
    }
  });

  // مراقبة حقل كلمة المرور وحقل نوع التسجيل
  const password = watch('password');
  const registrationType = watch('registrationType');

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    // 1. تجميع البيانات الصحيحة بناءً على نوع التسجيل
    const registrationData: RegisterData = {
      email: data.email,
      password: data.password,
      name: data.name,
      phone: data.phone,
      registrationType: data.registrationType as 'subscriber' | 'trainee' | 'lawyer', // التأكد من النوع
      // إضافة الحقول الشرطية
      ...(data.registrationType === 'trainee' && { registrationId: data.registrationId }),
      ...(data.registrationType === 'lawyer' && { registrationId: data.registrationId }),
      ...(data.registrationType === 'subscriber' && { 
        qualification: data.qualification,
        graduation_year: Number(data.graduation_year) // تحويل إلى رقم
      }),
    };

    const promise = authService.register(registrationData);

    toast.promise(promise, {
      loading: 'جاري إنشاء الحساب...',
      success: () => {
        setIsSuccess(true);
        // !! تعديل: رسالة نجاح جديدة
        return 'تم إرسال طلب التسجيل بنجاح!';
      },
      error: (err) => err.response?.data?.message || 'فشل التسجيل. يرجى التحقق من بياناتك.',
    });
  };

  // 2. عرض رسالة النجاح المخصصة
  if (isSuccess) {
    return (
      <div className={styles.pageContainer}>
        <div className={`${styles.formContainer} ${styles.successContainer}`}>
          <h2 className={styles.title}>تم استلام طلبك</h2>
          <div className={styles.successMessage}>
            <p>شكراً لتسجيلك. سيقوم المشرف بمراجعة طلبك.</p>
            <p>سيتم إعلامك عبر البريد الإلكتر الإلكتروني عند تفعيل حسابك.</p>
          </div>
          <p className={styles.linkText}>
            <Link to="/login">العودة إلى الصفحة الرئيسية</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.formContainer}>
        <h2 className={styles.title}>إنشاء حساب جديد</h2>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          
          {/* 3. الحقول الأساسية */}
          <div className={styles.formField}>
            <label htmlFor="name" className={styles.label}>الاسم الكامل</label>
            <input id="name" type="text" {...register('name', { required: 'الاسم الكامل مطلوب' })} className={styles.input} />
            {errors.name && <p className={styles.error}>{errors.name.message}</p>}
          </div>
          {/* 3. الحقول الأساسية */}
          <div className={styles.formField}>
            <label htmlFor="phone" className={styles.label}>رقم التلفون</label>
            <input id="phone" type="text" {...register('phone', { required: 'رقم التلفون مطلوب' })} className={styles.input} />
            {errors.phone && <p className={styles.error}>{errors.phone.message}</p>}
          </div>

          <div className={styles.formField}>
            <label htmlFor="email" className={styles.label}>البريد الإلكتروني</label>
            <input id="email" type="email" {...register('email', { required: 'البريد الإلكتروني مطلوب' })} className={styles.input} />
            {errors.email && <p className={styles.error}>{errors.email.message}</p>}
          </div>

          <div className={styles.formField}>
            <label htmlFor="password" className={styles.label}>كلمة المرور</label>
            <input id="password" type="password" {...register('password', { required: 'كلمة المرور مطلوبة', minLength: { value: 8, message: 'يجب أن تكون 8 أحرف على الأقل' } })} className={styles.input} />
            {errors.password && <p className={styles.error}>{errors.password.message}</p>}
          </div>

          <div className={styles.formField}>
            <label htmlFor="confirmPassword" className={styles.label}>تأكيد كلمة المرور</label>
            <input id="confirmPassword" type="password" {...register('confirmPassword', { required: 'التأكيد مطلوب', validate: value => value === password || 'كلمتا المرور غير متطابقتين' })} className={styles.input} />
            {errors.confirmPassword && <p className={styles.error}>{errors.confirmPassword.message}</p>}
          </div>

          {/* 4. اختيار نوع التسجيل */}
          <div className={styles.formField}>
            <label htmlFor="registrationType" className={styles.label}>أرغب في التسجيل كـ:</label>
            <select id="registrationType" {...register('registrationType', { required: 'يرجى اختيار نوع التسجيل' })} className={styles.input}>
              <option value="" disabled>-- اختر نوع التسجيل --</option>
              <option value="subscriber">مشترك جديد</option>
              <option value="trainee">متدرب (حساب موجود)</option>
              <option value="lawyer">محامي (حساب موجود)</option>
            </select>
            {errors.registrationType && <p className={styles.error}>{errors.registrationType.message}</p>}
          </div>

          {/* 5. عرض الحقول الديناميكية */}
          {registrationType === 'subscriber' && (
            <>
              <div className={styles.formField}>
                <label htmlFor="qualification" className={styles.label}>المؤهل العلمي</label>
                <input id="qualification" type="text" {...register('qualification', { required: 'المؤهل مطلوب' })} className={styles.input} />
                {errors.qualification && <p className={styles.error}>{errors.qualification.message}</p>}
              </div>
              <div className={styles.formField}>
                <label htmlFor="graduation_year" className={styles.label}>سنة التخرج</label>
                <input id="graduation_year" type="number" {...register('graduation_year', { required: 'سنة التخرج مطلوبة' })} className={styles.input} />
                {errors.graduation_year && <p className={styles.error}>{errors.graduation_year.message}</p>}
              </div>
            </>
          )}

          {(registrationType === 'trainee' || registrationType === 'lawyer') && (
            <div className={styles.formField}>
              <label htmlFor="registrationId" className={styles.label}>
                {registrationType === 'trainee' ? 'رقم قيد التدريب' : 'رقم القيد بالنقابة'}
              </label>
              <input id="registrationId" type="text" {...register('registrationId', { required: 'رقم القيد مطلوب للتحقق' })} className={styles.input} />
              {errors.registrationId && <p className={styles.error}>{errors.registrationId.message}</p>}
            </div>
          )}

          <button type="submit" disabled={isSubmitting || !registrationType} className={styles.submitButton}>
            {isSubmitting ? 'جاري إرسال الطلب...' : 'إرسال طلب التسجيل'}
          </button>
        </form>
        <p className={styles.linkText}>
          لديك حساب بالفعل؟ <Link to="/login">سجل دخولك</Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
